import React, { ComponentType, VFC } from 'react';

import { Box } from '@tanium/react-box';
import { Stack, StackChild } from '@tanium/react-stack';

import { ExtendAndOverride } from '../../utils/ExtendAndOverride';
import { Either } from './Either';
import { EmptyState } from './EmptyState';
import { emptyStateProps } from './emptyStateProps';
import { StatusProps } from './StatusProps';
import { useContainerDimensions } from './useContainerDimensions';

/**
 * Props to enable specifying chart dimensions.
 */
export interface ChartContainerProps {
  /**
   * The total height of the chart container. If not specified, a default height will be used.
   */
  height?: string | number;
  /**
   * The total width of the chart container. If not specified, the chart will fill the available
   * width.
   */
  width?: string | number;
}

/**
 * Props injected into a component wrapped by `withChartContainer`.
 */
export interface ChartContainerInjectedProps {
  /**
   * The computed height of the chart content in pixels.
   */
  height: number;
  /**
   * The computed width of the chart content in pixels.
   */
  width: number;
}

type WithChartContainerHelperProps<TInnerProps, TDataProps extends keyof TInnerProps> = ExtendAndOverride<
  Either<StatusProps, Pick<TInnerProps, TDataProps>> & ChartContainerProps,
  TInnerProps
>;

type FooterRenderFn<TRenderFnProps> = (props: TRenderFnProps) => React.ReactNode;

export type FooterRenderFnArray<TRenderFnProps> = readonly FooterRenderFn<TRenderFnProps>[];

interface WithChartContainerOptions<TRenderFnProps> {
  /**
   * Whether to wrap the chart in an absolutely positioned container. This may be necessary to avoid
   * a feedback loop where the chart's dimensions affect those of the parent, which in turn affects
   * the calculated height and width of the chart, etc. If the inner chart does not use the computed
   * height/width parameters (e.g., it fills its container instead), this should be set to false.
   *
   * Defaults to true.
   */
  absolutePositioning?: boolean;
  /**
   * Whether to render the chart even if one of the calculated dimensions is zero. Set this to true
   * if the chart is expected to have an intrinsic size regardless of its container.
   *
   * Defaults to false.
   */
  allowZeroDimension?: boolean;
  /**
   * The default height of the chart if not specified by the consumer. Defaults to 300.
   */
  defaultHeight?: string | number;
  /**
   * An array of renderers used to render "footer" content outside the bounds of the main chart
   * content. This content is rendered after the chart content. The footer's dimensions count toward
   * the specified height and width provided by the consumer, but is subtracted from those
   * calculated values passed to the inner wrapped component.
   */
  footerRenderFns?: FooterRenderFnArray<TRenderFnProps>;
}

export const withChartContainer = <
  TInnerProps extends unknown,
  TAdditionalProps,
  TDataProps extends keyof TInnerProps = never,
>(
  InnerComponent: React.ComponentType<TInnerProps>,
  {
    absolutePositioning = true,
    allowZeroDimension = false,
    defaultHeight = 300,
    footerRenderFns,
  }: WithChartContainerOptions<TAdditionalProps> = {},
): ComponentType<WithChartContainerHelperProps<TInnerProps, TDataProps> & TAdditionalProps> => {
  const ContainerHelper: VFC<WithChartContainerHelperProps<TInnerProps, TDataProps> & TAdditionalProps> = ({
    height: heightProp = defaultHeight,
    width: widthProp,
    status,
    statusMessage,
    ...props
  }) => {
    const { containerRef, dimensions, hasResizeObserverHeight } = useContainerDimensions({
      height: heightProp,
      width: widthProp,
    });
    const outerDimensions = { height: heightProp, width: widthProp };

    if (status) {
      if (status in emptyStateProps) {
        return (
          <EmptyState style={outerDimensions} {...emptyStateProps[status]}>
            {statusMessage}
          </EmptyState>
        );
      }

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console -- dev only warning
        console.error(`WARNING: "${status}" is not a valid chart status and will be ignored.`);
      }
    }

    const innerContent =
      allowZeroDimension || (dimensions.width > 0 && dimensions.height > 0 && hasResizeObserverHeight) ? (
        <InnerComponent {...(props as unknown as TInnerProps)} {...dimensions} />
      ) : null;

    const wrappedContent = absolutePositioning ? (
      // The style prop is used to set height and width throughout this component to avoid creating
      // too many classes if the specified height/width change dynamically
      <Box position="absolute" style={dimensions}>
        {innerContent}
      </Box>
    ) : (
      innerContent
    );

    return (
      <Stack style={outerDimensions} position="relative" direction="column">
        {/* Safari fix - `basis of 100%` is needed so that the child component can properly calculate the 100% height */}
        <StackChild grow={1} shrink={1} basis="100%">
          <Box height="100%" width="100%" ref={containerRef}>
            {wrappedContent}
          </Box>
        </StackChild>
        {footerRenderFns?.map((fn, index) => (
          // eslint-disable-next-line react/no-array-index-key -- array is immutable for duration of component lifetime
          <React.Fragment key={index}>{fn(props as unknown as TAdditionalProps)}</React.Fragment>
        ))}
      </Stack>
    );
  };

  ContainerHelper.displayName = `WithChartDimensions(${InnerComponent.displayName ?? 'Component'})`;

  return ContainerHelper;
};
